// <filter-dropdown> — single-open dropdown shell. Manages open state,
// outside-click close, and Escape-to-close.

const OPEN_ATTR = 'open';

class FilterDropdown extends HTMLElement {
  connectedCallback() {
    this.trigger = this.querySelector('[data-filter-dropdown-trigger]');
    this.panel = this.querySelector('[data-filter-dropdown-panel]');
    if (!this.trigger || !this.panel) return;

    this.trigger.addEventListener('click', this.#onTriggerClick);
    document.addEventListener('click', this.#onDocumentClick);
    document.addEventListener('keydown', this.#onKeyDown);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.#onDocumentClick);
    document.removeEventListener('keydown', this.#onKeyDown);
  }

  get isOpen() {
    return this.hasAttribute(OPEN_ATTR);
  }

  open() {
    document.querySelectorAll('filter-dropdown[open]').forEach((el) => {
      if (el !== this) el.close();
    });
    this.setAttribute(OPEN_ATTR, '');
    this.trigger?.setAttribute('aria-expanded', 'true');
  }

  close() {
    this.removeAttribute(OPEN_ATTR);
    this.trigger?.setAttribute('aria-expanded', 'false');
    // Hide any open L2 flyouts within the parent spec-filter-component.
    const spec = this.closest('spec-filter-component');
    if (spec) {
      spec.querySelectorAll('[data-spec-level="2"]').forEach((el) => {
        el.setAttribute('hidden', '');
      });
    }
  }

  toggle() {
    this.isOpen ? this.close() : this.open();
  }

  #onTriggerClick = (e) => {
    e.stopPropagation();
    this.toggle();
  };

  #onDocumentClick = (e) => {
    if (!this.isOpen) return;
    // Check if click is inside this dropdown or a sibling L2 flyout.
    const spec = this.closest('spec-filter-component');
    if (spec && spec.contains(e.target)) return;
    if (this.contains(e.target)) return;
    this.close();
  };

  #onKeyDown = (e) => {
    if (e.key === 'Escape' && this.isOpen) {
      this.close();
      this.trigger?.focus();
    }
  };
}

if (!customElements.get('filter-dropdown')) {
  customElements.define('filter-dropdown', FilterDropdown);
}

// <spec-filter-component> — handles drill-down navigation (L1 → L2 flyout),
// single-active enforcement (only one spec value at a time), and chip
// removal. Relies on the outer <facets-form-component> for AJAX submit.

class SpecFilterComponent extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
  }

  #onClick = (e) => {
    const target = e.target.closest(
      '[data-spec-show-key], [data-spec-back], [data-spec-select], [data-spec-remove]'
    );
    if (!target) return;

    if (target.hasAttribute('data-spec-show-key')) {
      this.#showLevel2(target.dataset.specShowKey);
    } else if (target.hasAttribute('data-spec-select')) {
      this.#selectValue(target);
    } else if (target.hasAttribute('data-spec-remove')) {
      this.#removeValue();
    }
  };

  #showLevel2(key) {
    // Hide any other open L2, then show the requested one.
    this.querySelectorAll('[data-spec-level="2"]').forEach((el) => {
      el.setAttribute('hidden', '');
    });
    const l2 = this.querySelector(`[data-spec-level="2"][data-spec-key="${key}"]`);
    if (!l2) return;

    // Align L2's top edge with the parent dimension button so the flyout
    // visually connects to its trigger. offsetTop resolves against the
    // positioned <filter-dropdown> ancestor (which is also the L2's
    // offsetParent), so they share the same coordinate space.
    const button = this.querySelector(`[data-spec-show-key="${key}"]`);
    if (button) {
      l2.style.top = button.offsetTop + 'px';
    }

    l2.removeAttribute('hidden');
  }

  #selectValue(btn) {
    const facetsForm = this.closest('facets-form-component');
    if (!facetsForm) return;

    const form = facetsForm.querySelector('form');
    if (!form) return;

    const paramName = btn.dataset.paramName;
    const value = btn.dataset.value;

    // Single-active: remove all existing spec hidden inputs.
    form.querySelectorAll('input[data-spec-input]').forEach((inp) => inp.remove());

    // Add a hidden input for the selected value.
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = paramName;
    input.value = value;
    input.setAttribute('data-spec-input', '');
    form.appendChild(input);

    // Close dropdown and submit.
    this.querySelector('filter-dropdown')?.close();

    // Use the public updateFilters method on <facets-form-component>.
    if (typeof facetsForm.updateFilters === 'function') {
      facetsForm.updateFilters();
    }
  }

  #removeValue() {
    const facetsForm = this.closest('facets-form-component');
    if (!facetsForm) return;

    const form = facetsForm.querySelector('form');
    if (!form) return;

    // Remove all spec hidden inputs.
    form.querySelectorAll('input[data-spec-input]').forEach((inp) => inp.remove());

    this.querySelector('filter-dropdown')?.close();

    if (typeof facetsForm.updateFilters === 'function') {
      facetsForm.updateFilters();
    }
  }
}

if (!customElements.get('spec-filter-component')) {
  customElements.define('spec-filter-component', SpecFilterComponent);
}

// Mobile drawer — deferred submit.
// "Show results" reads the drawer's form, calls updateFilters once, and
// closes the dialog. "Clear all" resets all inputs and submits.

// Dynamic result count for the mobile drawer "Show results" button.
// On input change, fetches section HTML with the current form data and
// extracts the updated product count from the response.

let countTimer = null;
let countAbort = null;

function updateDrawerCount(form) {
  clearTimeout(countTimer);
  countTimer = setTimeout(async () => {
    const facetsForm = form.closest('facets-form-component');
    if (!facetsForm) return;

    const sectionId = facetsForm.getAttribute('section-id');
    if (!sectionId) return;

    const formData = new FormData(form);
    const url = new URL(window.location.pathname, window.location.origin);
    for (const [key, value] of formData.entries()) {
      if ((key === 'filter.v.price.gte' || key === 'filter.v.price.lte') && value === '') continue;
      url.searchParams.append(key, value);
    }
    url.searchParams.set('section_id', sectionId);

    if (countAbort) countAbort.abort();
    countAbort = new AbortController();

    try {
      const res = await fetch(url.toString(), { signal: countAbort.signal });
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const countEl = doc.querySelector('.fs-filterbar__count');
      const applyBtn = form.querySelector('[data-drawer-apply]');
      if (countEl && applyBtn) {
        const countText = countEl.textContent.trim();
        applyBtn.textContent = `Show ${countText}`;
      }
    } catch (e) {
      if (e.name !== 'AbortError') throw e;
    }
  }, 350);
}

document.addEventListener('change', (e) => {
  const drawer = e.target.closest('.fs-filterbar__drawer');
  if (!drawer) return;
  const form = e.target.closest('form');
  if (form) updateDrawerCount(form);
});

document.addEventListener('input', (e) => {
  if (!(e.target instanceof HTMLInputElement)) return;
  if (e.target.type !== 'text') return;
  const drawer = e.target.closest('.fs-filterbar__drawer');
  if (!drawer) return;
  const form = e.target.closest('form');
  if (form) updateDrawerCount(form);
});

document.addEventListener('click', (e) => {
  const applyBtn = e.target.closest('[data-drawer-apply]');
  if (applyBtn) {
    const facetsForm = applyBtn.closest('facets-form-component');
    if (facetsForm && typeof facetsForm.updateFilters === 'function') {
      facetsForm.updateFilters();
    }
    const dialog = applyBtn.closest('dialog');
    const dialogComponent = dialog?.closest('dialog-component');
    if (dialogComponent && typeof dialogComponent.closeDialog === 'function') {
      dialogComponent.closeDialog();
    }
    return;
  }

  const clearBtn = e.target.closest('[data-drawer-clear]');
  if (clearBtn) {
    const form = clearBtn.closest('form');
    if (!form) return;

    // Uncheck all checkboxes and radios.
    form.querySelectorAll('input[type="checkbox"], input[type="radio"]').forEach((inp) => {
      inp.checked = false;
    });
    // Clear text inputs (price).
    form.querySelectorAll('input[type="text"]').forEach((inp) => {
      inp.value = '';
    });
    // Remove hidden spec inputs.
    form.querySelectorAll('input[data-spec-input]').forEach((inp) => inp.remove());

    // Submit to clear all filters.
    const facetsForm = clearBtn.closest('facets-form-component');
    if (facetsForm && typeof facetsForm.updateFilters === 'function') {
      facetsForm.updateFilters();
    }
    const dialog = clearBtn.closest('dialog');
    const dialogComponent = dialog?.closest('dialog-component');
    if (dialogComponent && typeof dialogComponent.closeDialog === 'function') {
      dialogComponent.closeDialog();
    }
  }
});
